import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { KENTRO } from "@/lib/constants";
import { trackIaNav } from "@/lib/analytics";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { LocaleSwitcher } from "@/i18n/LocaleSwitcher";
import { useT, useLocale } from "@/i18n/LocaleProvider";
import { withLocale } from "@/i18n/types";
import logoBranca from "@/assets/kentro-logo-branca.svg.asset.json";
import logoRoxa from "@/assets/kentro-logo-roxa.svg.asset.json";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const t = useT();
  const locale = useLocale();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Aplica prefixo de locale a links internos
  const lp = (path: string) => withLocale(path, locale);

  const NAV = [
    { label: "Omnichannel", to: lp("/omnichannel") },
    { label: t.nav.ia, to: lp("/ia"), trackIa: true },
    { label: t.nav.cases, to: lp("/cases") },
    { label: t.nav.blog, to: lp("/blog"), gold: true },
    { label: "Academy", href: KENTRO.links.academy, external: true },
    { label: "Parceria", to: lp("/parceria") },
    { label: "Sobre", to: lp("/sobre") },
    { label: "Equipe", to: lp("/equipe"), highlight: true },
  ];

  return (
    <header className="fixed top-0 z-40 w-full border-b border-white/5 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link to={lp("/")} className="flex shrink-0 items-center gap-2" onClick={() => setOpen(false)} aria-label="Kentro Sistemas">
          <img
            src={logoBranca.url}
            alt="Kentro Sistemas"
            width={140}
            height={40}
            className="h-9 w-auto block [html.light_&]:hidden"
            loading="eager"
            decoding="async"
          />
          <img
            src={logoRoxa.url}
            alt="Kentro Sistemas"
            width={140}
            height={40}
            className="h-9 w-auto hidden [html.light_&]:block"
            loading="eager"
            decoding="async"
          />
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-0.5 lg:flex">
          {NAV.map((l) =>
            l.external ? (
              <a
                key={l.label}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md px-2.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.label}
                to={l.to!}
                onClick={l.trackIa ? () => trackIaNav("header_desktop") : undefined}
                className={
                  l.highlight
                    ? "gradient-primary rounded-full px-3.5 py-2 text-sm font-semibold text-white shadow-glow transition hover:opacity-95"
                    : l.gold
                      ? "gold-outline mx-1 rounded-full px-3 py-1.5 text-sm font-semibold text-foreground"
                      : "rounded-md px-2.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                }
                activeProps={l.highlight ? { className: "text-white" } : { className: "text-foreground" }}
              >
                {l.label}
              </Link>
            ),
          )}
        </nav>

        <div className="hidden items-center gap-1 lg:flex">
          <LocaleSwitcher />
          <ThemeToggle />
          <Button asChild size="sm" className="gradient-primary text-white shadow-glow hover:opacity-95">
            <Link to={lp("/contato")}>{t.nav.demoCta}</Link>
          </Button>
        </div>

        <div className="flex items-center gap-1 lg:hidden">
          <LocaleSwitcher compact />
          <ThemeToggle />
          <button
            className="inline-flex size-11 items-center justify-center rounded-md text-foreground"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
            aria-expanded={open}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/5 bg-background/95 backdrop-blur-xl lg:hidden">
          <nav className="flex flex-col gap-1 px-4 py-4">
            {NAV.map((l) =>
              l.external ? (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="rounded-md px-3 py-3 text-sm font-medium text-muted-foreground hover:bg-white/5 hover:text-foreground"
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.label}
                  to={l.to!}
                  onClick={() => {
                    if (l.trackIa) trackIaNav("header_mobile");
                    setOpen(false);
                  }}
                  className={
                    l.highlight
                      ? "gradient-primary rounded-md px-3 py-3 text-sm font-semibold text-white shadow-glow"
                      : l.gold
                        ? "gold-outline rounded-md px-3 py-3 text-sm font-semibold text-foreground"
                        : "rounded-md px-3 py-3 text-sm font-medium text-muted-foreground hover:bg-white/5 hover:text-foreground"
                  }
                >
                  {l.label}
                </Link>
              ),
            )}
            <div className="mt-2 grid gap-2">
              <Button asChild className="gradient-primary text-white">
                <Link to={lp("/contato")} onClick={() => setOpen(false)}>
                  {t.nav.demoCta}
                </Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
